import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Card, Stack, Slider, Button,
  CircularProgress, Chip, Autocomplete, TextField, Checkbox,
  List, ListItemButton, ListItemText, ListItemIcon, Divider, MenuItem,
  Tabs, Tab, Tooltip, IconButton,
} from '@mui/material'
import {
  PlayArrow, Pause, Replay, Speed, CheckBoxOutlineBlank, CheckBox, Route, AccessTime,
  PauseCircle, FlagCircle, LocationOn, Fullscreen, FullscreenExit,
} from '@mui/icons-material'
import { MapContainer, Marker, Polyline, Popup, CircleMarker, useMap } from 'react-leaflet'
import MapTileLayer from '../../components/MapTileLayer'
import { SmoothMarker } from '../../components/SmoothMarker'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { subHours, formatISO } from 'date-fns'
import { useSearchParams } from 'react-router-dom'
import { useDevices, Device } from '../../hooks/useDevices'
import { useQueries } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { unwrapList } from '../../lib/unwrap'
import { createVehicleIcon } from '../../lib/vehicleIcon'
import { fmtDateTimeFull, fmtTime } from '../../lib/dateFormat'
import '../../lib/vehicleIcon.css'

const COLORS = ['#34A8E0', '#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6']

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

interface GpsPoint { latitude: number; longitude: number; speed: number; heading: number; time: string }

interface Stop {
  index: number
  latitude: number
  longitude: number
  startTime: string
  endTime: string
  durationSec: number
  distFromPrevKm: number
  pointStart: number
  pointEnd: number
}

function detectStops(points: GpsPoint[], minDurationSec: number): Stop[] {
  const stops: Stop[] = []
  let i = 0
  while (i < points.length) {
    if (points[i].speed > 0) { i++; continue }
    const start = i
    while (i < points.length && points[i].speed === 0) i++
    const end = i - 1
    const startTime = new Date(points[start].time).getTime()
    const endTime = new Date(points[end].time).getTime()
    const dur = (endTime - startTime) / 1000
    if (dur >= minDurationSec) {
      const prev = stops.length > 0 ? stops[stops.length - 1] : null
      const dist = prev ? haversineKM(prev.latitude, prev.longitude, points[start].latitude, points[start].longitude) : 0
      stops.push({
        index: stops.length + 1,
        latitude: points[start].latitude,
        longitude: points[start].longitude,
        startTime: points[start].time,
        endTime: points[end].time,
        durationSec: dur,
        distFromPrevKm: dist,
        pointStart: start,
        pointEnd: end,
      })
    }
  }
  return stops
}

function haversineKM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function fmtDuration(sec: number): string {
  if (sec < 60) return `${Math.round(sec)}s`
  const m = Math.floor(sec / 60)
  const s = Math.round(sec % 60)
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

function stopColor(sec: number): string {
  if (sec < 300) return '#f59e0b'
  if (sec < 1800) return '#f97316'
  return '#ef4444'
}

function stopSize(sec: number): number {
  if (sec < 300) return 30
  if (sec < 1800) return 36
  return 42
}

function FlyTo({ position }: { position: [number, number] | null }) {
  const map = useMap()
  useEffect(() => {
    if (position) map.flyTo(position, 16, { duration: 0.8 })
  }, [position?.[0], position?.[1]])
  return null
}

function FitPath({ path, triggerKey }: { path: [number, number][]; triggerKey: string }) {
  const map = useMap()
  useEffect(() => {
    if (path.length === 0) return
    if (path.length === 1) {
      map.flyTo(path[0], 15, { duration: 0.8 })
    } else {
      map.flyToBounds(L.latLngBounds(path), { padding: [50, 50], maxZoom: 16, duration: 0.8 })
    }
  }, [triggerKey])
  return null
}

function flagIcon(label: string, color: string) {
  return L.divIcon({
    className: '',
    html: `<div style="display:flex;align-items:center;gap:4px">
      <div style="background:${color};color:#fff;border-radius:4px;padding:2px 8px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.25);border:2px solid #fff">${label}</div>
    </div>`,
    iconSize: [80, 24],
    iconAnchor: [0, 12],
  })
}

export default function TripPlaybackPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { data: devicesRaw } = useDevices()
  const devices = unwrap<Device>(devicesRaw)

  const urlDevice = searchParams.get('device')
  const urlFrom = searchParams.get('from')
  const urlTo = searchParams.get('to')

  const [selectedDevices, setSelectedDevices] = useState<Device[]>([])
  const [isFullscreen, setIsFullscreen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {})
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {})
    }
  }, [])

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  // Browse range: controls the trip list sidebar — always a full day so all trips are visible
  const [browseFrom, setBrowseFrom] = useState<Date>(() => {
    if (urlFrom) {
      const d = new Date(urlFrom)
      d.setHours(0, 0, 0, 0)
      return d
    }
    return subHours(new Date(), 24)
  })
  const [browseTo, setBrowseTo] = useState<Date>(() => {
    if (urlTo) {
      const d = new Date(urlTo)
      d.setHours(23, 59, 59, 999)
      return d
    }
    return new Date()
  })

  // Playback range: controls the GPS track on the map (narrow — one trip at a time)
  const [playFrom, setPlayFrom] = useState<Date>(urlFrom ? new Date(urlFrom) : subHours(new Date(), 24))
  const [playTo, setPlayTo] = useState<Date>(urlTo ? new Date(urlTo) : new Date())

  const [sidebarTab, setSidebarTab] = useState(0)
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null)
  const [activeTripId, setActiveTripId] = useState<string | null>(null)
  const [minStopMin, setMinStopMin] = useState(2)

  useEffect(() => {
    if (urlDevice && devices.length > 0 && selectedDevices.length === 0) {
      const found = devices.find((d) => d.id === urlDevice)
      if (found) setSelectedDevices([found])
    }
  }, [urlDevice, devices])

  const browseFromISO = useMemo(() => formatISO(browseFrom), [browseFrom])
  const browseToISO = useMemo(() => formatISO(browseTo), [browseTo])
  const playFromISO = useMemo(() => formatISO(playFrom), [playFrom])
  const playToISO = useMemo(() => formatISO(playTo), [playTo])

  // Trip list uses the BROWSE range (wide)
  const tripQueries = useQueries({
    queries: selectedDevices.map((d) => ({
      queryKey: ['trips', d.id, browseFromISO, browseToISO],
      queryFn: () =>
        api.get('/trips', { params: { device_id: d.id, from: browseFromISO, to: browseToISO } })
          .then((r) => unwrapList(r.data)),
      enabled: !!d.id,
    })),
  })

  const allTrips = useMemo(() => {
    return selectedDevices.flatMap((d, i) =>
      ((tripQueries[i]?.data ?? []) as Array<{ id?: string; started_at: string; ended_at?: string | null; device_id?: string } & Record<string, unknown>>)
        .map((trip) => ({ ...trip, device_id: trip.device_id ?? d.id }))
    )
  }, [selectedDevices, tripQueries.map((q) => q.data)])

  // GPS track uses the PLAYBACK range (narrow — one trip)
  const trackQueries = useQueries({
    queries: selectedDevices.map((d) => ({
      queryKey: ['analytics', 'gps-track', { device_id: d.id, from: playFromISO, to: playToISO, limit: 5000 }],
      queryFn: () =>
        api.get<{ points: GpsPoint[] }>('/analytics/gps/track', {
          params: { device_id: d.id, from: playFromISO, to: playToISO, limit: 5000 },
        }).then((r) => {
          const d = r.data as any
          if (Array.isArray(d)) return d
          return d?.points ?? d?.data ?? []
        }),
      enabled: !!d.id,
    })),
  })

  const isLoading = trackQueries.some((q) => q.isLoading)

  const deviceTracks = useMemo(() =>
    selectedDevices.map((d, i) => ({
      device: d,
      color: COLORS[i % COLORS.length],
      points: (trackQueries[i]?.data ?? []) as GpsPoint[],
      path: ((trackQueries[i]?.data ?? []) as GpsPoint[]).map(
        (p) => [p.latitude, p.longitude] as [number, number]
      ),
    })),
    [selectedDevices, trackQueries.map((q) => q.data)]
  )

  const totalPoints = deviceTracks.reduce((s, t) => s + t.points.length, 0)
  const maxLen = Math.max(...deviceTracks.map((t) => t.points.length), 1)
  const allPath = useMemo(() => deviceTracks.flatMap((t) => t.path), [deviceTracks])

  const [idx, setIdx] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState(1)

  const allStops = useMemo(() => {
    return deviceTracks.flatMap((t, ti) => {
      if (t.points.length === 0) return []
      return detectStops(t.points, minStopMin * 60).map((s) => ({
        ...s,
        deviceName: t.device.name,
        deviceColor: t.color,
        trackIndex: ti,
      }))
    })
  }, [deviceTracks, minStopMin])

  const totalStopSec = useMemo(() => allStops.reduce((s, st) => s + st.durationSec, 0), [allStops])

  // Reset playback when playback range changes
  useEffect(() => { setIdx(0); setPlaying(false) }, [playFromISO, playToISO, selectedDevices.length])

  useEffect(() => {
    if (!playing || maxLen <= 1) return
    const interval = setInterval(() => {
      setIdx((p) => {
        const next = p + speed
        if (next >= maxLen) { setPlaying(false); return maxLen - 1 }
        return next
      })
    }, 200)
    return () => clearInterval(interval)
  }, [playing, speed, maxLen])

  const center = useMemo<[number, number]>(() => {
    for (const t of deviceTracks) {
      if (t.path.length > 0) return t.path[0]
    }
    return [33.3152, 44.3661]
  }, [deviceTracks])

  // Click a trip → set playback range to that trip's start/end, keep browse range unchanged
  const handleTripClick = useCallback((trip: { id?: string; started_at: string; ended_at?: string | null }) => {
    setPlayFrom(new Date(trip.started_at))
    setPlayTo(trip.ended_at ? new Date(trip.ended_at) : new Date())
    setActiveTripId(trip.id ?? null)
    setIdx(0)
    setPlaying(false)
    setFlyTarget(null) // reset so FitPath takes over
  }, [])

  // When browse range changes via date pickers, also reset playback range
  const handleBrowseFromChange = useCallback((v: Date | null) => {
    if (!v) return
    setBrowseFrom(v)
    setPlayFrom(v)
    setActiveTripId(null)
  }, [])
  const handleBrowseToChange = useCallback((v: Date | null) => {
    if (!v) return
    setBrowseTo(v)
    setPlayTo(v)
    setActiveTripId(null)
  }, [])

  const handleStopClick = useCallback((stop: Stop) => {
    setFlyTarget([stop.latitude, stop.longitude])
  }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        ref={containerRef}
        sx={{
          height: isFullscreen ? '100vh' : 'calc(100vh - 140px)',
          display: 'flex', flexDirection: 'column',
          bgcolor: isFullscreen ? '#fff' : 'transparent',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isFullscreen ? 1 : 2, px: isFullscreen ? 2 : 0, pt: isFullscreen ? 1 : 0 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
              {t('playback.title')}
            </Typography>
            <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
              {t('playback.selectSubtitle')}
            </Typography>
          </Box>
          <Tooltip title={isFullscreen ? t('playback.exitFullscreen') : t('playback.fullscreen')}>
            <IconButton onClick={toggleFullscreen} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>

        <Card sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', mx: isFullscreen ? 2 : 0 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <Autocomplete
              multiple size="small" options={devices}
              getOptionLabel={(d) => d.name || d.device_identifier}
              value={selectedDevices} onChange={(_, v) => setSelectedDevices(v)}
              disableCloseOnSelect
              slotProps={{ popper: { disablePortal: isFullscreen } }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBox fontSize="small" />} checked={selected} sx={{ mr: 1 }} />
                  {option.name}
                </li>
              )}
              renderInput={(params) => <TextField {...params} label={t('playback.devices')} placeholder={t('playback.selectDevices')} />}
              sx={{ minWidth: 280 }}
            />
            <DateTimePicker label={t('playback.from')} value={browseFrom} onChange={handleBrowseFromChange}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } }, popper: { disablePortal: isFullscreen } }} />
            <DateTimePicker label={t('playback.to')} value={browseTo} onChange={handleBrowseToChange}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } }, popper: { disablePortal: isFullscreen } }} />
            <Box sx={{ flex: 1 }} />
            <TextField label={t('playback.minStop')} type="number" size="small" value={minStopMin}
              onChange={(e) => setMinStopMin(Math.max(1, Math.min(60, Number(e.target.value))))}
              inputProps={{ min: 1, max: 60 }} sx={{ width: 130 }} />
            <Chip label={t('playback.points', { count: totalPoints })} size="small" />
            <Chip label={t('playback.stops', { count: allStops.length })} size="small"
              sx={{ bgcolor: allStops.length > 0 ? '#fef2f2' : undefined, color: allStops.length > 0 ? '#ef4444' : undefined, fontWeight: 600 }} />
            {totalStopSec > 0 && (
              <Chip label={t('playback.stopped', { duration: fmtDuration(totalStopSec) })} size="small" variant="outlined"
                sx={{ fontSize: 11, color: '#f97316', borderColor: '#f97316' }} />
            )}
          </Stack>
        </Card>

        {/* Main: Map + Sidebar */}
        <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0, px: isFullscreen ? 2 : 0 }}>
          {/* Map */}
          <Card sx={{ flex: 1, position: 'relative', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            {isLoading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, bgcolor: 'rgba(255,255,255,0.6)' }}>
                <CircularProgress sx={{ color: '#34A8E0' }} />
              </Box>
            )}
            <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }}>
              <MapTileLayer />
              <FlyTo position={flyTarget} />
              {!flyTarget && <FitPath path={allPath} triggerKey={playFromISO + playToISO + totalPoints} />}

              {/* Route polylines */}
              {deviceTracks.map((track) => (
                track.path.length > 0 && (
                  <Polyline key={track.device.id} positions={track.path}
                    pathOptions={{ color: track.color, weight: 4, opacity: 0.7 }} />
                )
              ))}

              {/* Start marker */}
              {deviceTracks.map((track) => {
                if (track.path.length === 0) return null
                return (
                  <Marker key={`start-${track.device.id}`} position={track.path[0]}
                    icon={flagIcon('START', '#10b981')}>
                    <Popup>
                      <strong>{t('playback.tripStart')} — {track.device.name}</strong><br />
                      {fmtDateTimeFull(track.points[0].time)}<br />
                      {track.points[0].latitude.toFixed(5)}, {track.points[0].longitude.toFixed(5)}
                    </Popup>
                  </Marker>
                )
              })}

              {/* End marker */}
              {deviceTracks.map((track) => {
                if (track.path.length < 2) return null
                const lastPt = track.points[track.points.length - 1]
                return (
                  <Marker key={`end-${track.device.id}`} position={track.path[track.path.length - 1]}
                    icon={flagIcon('END', '#ef4444')}>
                    <Popup>
                      <strong>{t('playback.tripEnd')} — {track.device.name}</strong><br />
                      {fmtDateTimeFull(lastPt.time)}<br />
                      {lastPt.latitude.toFixed(5)}, {lastPt.longitude.toFixed(5)}
                    </Popup>
                  </Marker>
                )
              })}

              {/* Playback cursor */}
              {deviceTracks.map((track) => {
                const pt = track.points[Math.min(idx, track.points.length - 1)]
                if (!pt) return null
                return (
                  <SmoothMarker key={`cursor-${track.device.id}`}
                    position={[pt.latitude, pt.longitude]}
                    icon={createVehicleIcon(track.device.vehicle_type, pt.heading, pt.speed > 0)}
                    duration={200}>
                    <Popup>
                      <strong>{track.device.name}</strong>
                      {track.device.vehicle_type && <><br />{t('map.type')}: {track.device.vehicle_type}</>}
                      <br />
                      <span style={{ color: track.color }}>{fmtDateTimeFull(pt.time)}</span>
                      <br />
                      {t('map.speed')}: {pt.speed} km/h · {t('map.heading')}: {pt.heading}&deg;
                    </Popup>
                  </SmoothMarker>
                )
              })}

              {/* Stop markers */}
              {allStops.map((stop) => {
                const sz = stopSize(stop.durationSec)
                const clr = stopColor(stop.durationSec)
                return (
                  <Marker key={`stop-${stop.trackIndex}-${stop.index}`}
                    position={[stop.latitude, stop.longitude]}
                    icon={L.divIcon({
                      className: '',
                      html: `<div style="position:relative;display:flex;flex-direction:column;align-items:center">
                        <div style="background:${clr};color:#fff;border-radius:50%;width:${sz}px;height:${sz}px;display:flex;align-items:center;justify-content:center;font-size:${sz > 36 ? 14 : 12}px;font-weight:700;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.35);line-height:1">${stop.index}</div>
                        <div style="background:rgba(0,0,0,0.75);color:#fff;border-radius:4px;padding:1px 6px;font-size:10px;font-weight:600;margin-top:2px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,0.2)">${fmtDuration(stop.durationSec)}</div>
                      </div>`,
                      iconSize: [sz + 20, sz + 22],
                      iconAnchor: [(sz + 20) / 2, sz / 2],
                    })}>
                    <Popup>
                      <div style={{ minWidth: 200 }}>
                        <strong style={{ fontSize: 14 }}>Stop #{stop.index}</strong>
                        <span style={{ float: 'right', background: clr, color: '#fff', borderRadius: 4, padding: '1px 8px', fontSize: 12, fontWeight: 700 }}>
                          {fmtDuration(stop.durationSec)}
                        </span>
                        <hr style={{ margin: '6px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
                        <table style={{ fontSize: 12, lineHeight: 1.6 }}>
                          <tbody>
                            <tr><td style={{ color: '#6b7280', paddingRight: 8 }}>{t('playback.arrived')}</td><td><strong>{fmtTime(stop.startTime)}</strong></td></tr>
                            <tr><td style={{ color: '#6b7280', paddingRight: 8 }}>{t('playback.departed')}</td><td><strong>{fmtTime(stop.endTime)}</strong></td></tr>
                            <tr><td style={{ color: '#6b7280', paddingRight: 8 }}>{t('playback.duration')}</td><td><strong>{fmtDuration(stop.durationSec)}</strong></td></tr>
                            <tr><td style={{ color: '#6b7280', paddingRight: 8 }}>{t('playback.location')}</td><td>{stop.latitude.toFixed(5)}, {stop.longitude.toFixed(5)}</td></tr>
                            {stop.distFromPrevKm > 0 && (
                              <tr><td style={{ color: '#6b7280', paddingRight: 8 }}>{t('playback.fromPrev')}</td><td>{stop.distFromPrevKm.toFixed(1)} km</td></tr>
                            )}
                          </tbody>
                        </table>
                        <div style={{ marginTop: 6 }}>
                          <a href={`https://www.google.com/maps?q=${stop.latitude},${stop.longitude}`}
                            target="_blank" rel="noopener" style={{ fontSize: 11 }}>{t('playback.openGoogleMaps')}</a>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )
              })}

              {/* Stop radius rings */}
              {allStops.map((stop) => (
                <CircleMarker key={`stop-ring-${stop.trackIndex}-${stop.index}`}
                  center={[stop.latitude, stop.longitude]}
                  radius={Math.min(20, 8 + stop.durationSec / 300)}
                  pathOptions={{ color: stopColor(stop.durationSec), fillColor: stopColor(stop.durationSec), fillOpacity: 0.12, weight: 2, dashArray: '4 4' }}
                />
              ))}
            </MapContainer>
          </Card>

          {/* Sidebar */}
          {selectedDevices.length >= 1 && (
            <Card sx={{ width: 320, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
              <Tabs value={sidebarTab} onChange={(_, v) => setSidebarTab(v)} variant="fullWidth"
                sx={{ borderBottom: '1px solid #e2e8f0', minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: 12, fontWeight: 600 } }}>
                <Tab icon={<Route sx={{ fontSize: 14 }} />} iconPosition="start" label={`${t('trips.title')} (${allTrips.length})`} />
                <Tab icon={<PauseCircle sx={{ fontSize: 14 }} />} iconPosition="start"
                  label={`${t('playback.stops', { count: allStops.length })}`}
                  sx={{ color: allStops.length > 0 ? '#ef4444' : undefined }} />
              </Tabs>

              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {/* Trips tab */}
                {sidebarTab === 0 && (
                  <>
                    {allTrips.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="#94a3b8">{t('playback.noTripsInRange')}</Typography>
                      </Box>
                    ) : (
                      <List dense disablePadding>
                        {allTrips.map((t: any, i: number) => {
                          const tripDevice = selectedDevices.find((d) => d.id === t.device_id)
                          const deviceColor = tripDevice ? COLORS[selectedDevices.indexOf(tripDevice) % COLORS.length] : COLORS[0]
                          const isActive = activeTripId === (t.id ?? null)
                          return (
                            <Box key={t.id ?? i}>
                              {i > 0 && <Divider />}
                              <ListItemButton
                                selected={isActive}
                                onClick={() => handleTripClick(t)}
                                sx={{
                                  py: 1,
                                  '&.Mui-selected': { bgcolor: '#EBF6FD', borderLeft: '3px solid #34A8E0' },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <Route sx={{ fontSize: 18, color: isActive ? '#34A8E0' : deviceColor }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" fontWeight={600} color="#1e293b">
                                      {t.distance_km?.toFixed(1) ?? '?'} km · {Math.round((t.duration_sec ?? 0) / 60)} min
                                    </Typography>
                                  }
                                  secondary={
                                    <Box component="span">
                                      {selectedDevices.length > 1 && tripDevice && (
                                        <Typography variant="caption" sx={{ color: deviceColor, fontWeight: 600, display: 'block' }}>
                                          {tripDevice.name}
                                        </Typography>
                                      )}
                                      <Typography variant="caption" color="#94a3b8" component="span">
                                        <AccessTime sx={{ fontSize: 10, mr: 0.3, verticalAlign: 'middle' }} />
                                        {fmtDateTimeFull(t.started_at)}
                                        {t.max_speed_kph > 0 && ` · max ${t.max_speed_kph.toFixed(0)} km/h`}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <PlayArrow sx={{ fontSize: 16, color: isActive ? '#34A8E0' : '#94a3b8' }} />
                              </ListItemButton>
                            </Box>
                          )
                        })}
                      </List>
                    )}
                  </>
                )}

                {/* Stops tab */}
                {sidebarTab === 1 && (
                  <>
                    {allStops.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <PauseCircle sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                        <Typography variant="body2" color="#94a3b8">
                          {t('playback.noStopsDetected', { min: minStopMin })}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip size="small" label={`${allStops.length} stops`}
                              sx={{ bgcolor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 11 }} />
                            <Chip size="small" label={`${fmtDuration(totalStopSec)} total`}
                              sx={{ bgcolor: '#fff', color: '#ef4444', fontWeight: 600, fontSize: 11, border: '1px solid #fecaca' }} />
                            {allStops.length > 0 && (
                              <Chip size="small"
                                label={`longest: ${fmtDuration(Math.max(...allStops.map((s) => s.durationSec)))}`}
                                sx={{ bgcolor: '#fff', color: '#f97316', fontWeight: 600, fontSize: 11, border: '1px solid #fed7aa' }} />
                            )}
                          </Stack>
                        </Box>
                        <List dense disablePadding>
                          {allStops.map((stop, i) => (
                            <Box key={`${stop.trackIndex}-${stop.index}`}>
                              {i > 0 && <Divider />}
                              <ListItemButton onClick={() => handleStopClick(stop)} sx={{ py: 1, px: 1.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Box sx={{
                                    width: 28, height: 28, borderRadius: '50%',
                                    bgcolor: stopColor(stop.durationSec), color: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 12, fontWeight: 700,
                                    border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                  }}>
                                    {stop.index}
                                  </Box>
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                      <Typography variant="body2" fontWeight={700} color="#1e293b">
                                        {fmtDuration(stop.durationSec)}
                                      </Typography>
                                      <Chip size="small" label={stop.durationSec >= 1800 ? t('playback.long') : stop.durationSec >= 300 ? t('playback.medium') : t('playback.short')}
                                        sx={{
                                          height: 18, fontSize: 9, fontWeight: 700,
                                          bgcolor: stopColor(stop.durationSec) + '18',
                                          color: stopColor(stop.durationSec),
                                        }} />
                                    </Stack>
                                  }
                                  secondary={
                                    <Box component="span">
                                      <Typography variant="caption" color="#64748b" component="span" sx={{ display: 'block' }}>
                                        {fmtTime(stop.startTime)} — {fmtTime(stop.endTime)}
                                      </Typography>
                                      <Typography variant="caption" color="#94a3b8" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.2 }}>
                                        <LocationOn sx={{ fontSize: 10 }} />
                                        {stop.latitude.toFixed(4)}, {stop.longitude.toFixed(4)}
                                        {stop.distFromPrevKm > 0 && ` · ${stop.distFromPrevKm.toFixed(1)} km ${t('playback.fromPrev')}`}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <Tooltip title={t('playback.openGoogleMaps')}>
                                  <FlagCircle sx={{ fontSize: 16, color: '#94a3b8', cursor: 'pointer' }}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      window.open(`https://www.google.com/maps?q=${stop.latitude},${stop.longitude}`, '_blank')
                                    }} />
                                </Tooltip>
                              </ListItemButton>
                            </Box>
                          ))}
                        </List>
                      </>
                    )}
                  </>
                )}
              </Box>
            </Card>
          )}
        </Box>

        {/* Playback controls */}
        {totalPoints > 0 && (
          <Card sx={{ p: 2, mt: 2, border: '1px solid #e2e8f0', mx: isFullscreen ? 2 : 0, mb: isFullscreen ? 2 : 0 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button variant="contained" size="small"
                startIcon={playing ? <Pause /> : <PlayArrow />}
                onClick={() => {
                  const nextPlaying = !playing
                  setPlaying(nextPlaying)
                  if (nextPlaying) {
                    analytics.track(EVENTS.TRIP_PLAYBACK_STARTED, {
                      device_count: selectedDevices.length,
                      total_points: totalPoints,
                    })
                  }
                }}
                sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}>
                {playing ? t('playback.pause') : t('playback.play')}
              </Button>
              <Button size="small" startIcon={<Replay />} onClick={() => { setIdx(0); setPlaying(false) }}>
                {t('playback.restart')}
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Speed sx={{ fontSize: 16, color: '#94a3b8' }} />
                <TextField select size="small" value={speed}
                  onChange={(e) => { const s = Number(e.target.value); setSpeed(s) }} sx={{ minWidth: 80 }}>
                  {[1, 2, 4, 8, 16].map((s) => (
                    <MenuItem key={s} value={s}>{s}x</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: 1, mx: 2 }}>
                <Slider value={idx} max={maxLen - 1} min={0}
                  onChange={(_, v) => setIdx(v as number)} size="small" />
              </Box>
              <Typography variant="caption" color="#64748b" sx={{ minWidth: 80 }}>
                {idx + 1} / {maxLen}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {deviceTracks.map((track) => (
                <Chip key={track.device.id} label={`${track.device.name} (${track.points.length})`}
                  size="small" sx={{ bgcolor: track.color, color: '#fff', fontWeight: 600 }} />
              ))}
            </Stack>
          </Card>
        )}
      </Box>
    </LocalizationProvider>
  )
}
